import { defaultHttpClient } from '../http/DefaultHttpClient.js';
import Random from '../../primitives/Random.js';
import { toHex } from '../../primitives/utils.js';
function defaultDeploymentId() {
    return `ts-sdk-${toHex(Random(16))}`;
}
const ARC_ERROR_STATUSES = new Set([
    'DOUBLE_SPEND_ATTEMPTED',
    'REJECTED',
    'INVALID',
    'MALFORMED',
    'MINED_IN_STALE_BLOCK'
]);
function transactionHex(tx) {
    try {
        return tx.toHexEF();
    }
    catch (error) {
        if (error.message ===
            'All inputs must have source transactions when serializing to EF format')
            return tx.toHex();
        throw error;
    }
}
function successfulArcResponse(data) {
    const { txid, extraInfo, txStatus, competingTxs } = data;
    const upperStatus = txStatus?.toUpperCase();
    const isOrphan = extraInfo?.toUpperCase().includes('ORPHAN') || upperStatus?.includes('ORPHAN');
    if (ARC_ERROR_STATUSES.has(upperStatus) || isOrphan) {
        const failure = {
            status: 'error',
            code: txStatus ?? 'UNKNOWN',
            txid,
            description: `${txStatus ?? ''} ${extraInfo ?? ''}`.trim()
        };
        if (competingTxs != null)
            failure.more = { competingTxs };
        return failure;
    }
    const response = {
        status: 'success',
        txid,
        message: `${txStatus} ${extraInfo}`
    };
    if (competingTxs != null)
        response.competingTxs = competingTxs;
    return response;
}
function parseArcFailureData(data) {
    if (typeof data !== 'string')
        return data;
    try {
        return JSON.parse(data);
    }
    catch {
        return data;
    }
}
function failedArcResponse(status, responseData) {
    const failure = {
        status: 'error',
        code: typeof status === 'number' || typeof status === 'string' ? status.toString() : 'ERR_UNKNOWN',
        description: 'Unknown error'
    };
    const data = parseArcFailureData(responseData);
    if (data == null || typeof data !== 'object')
        return failure;
    failure.more = data;
    if ('txid' in data && typeof data.txid === 'string')
        failure.txid = data.txid;
    if ('detail' in data && typeof data.detail === 'string')
        failure.description = data.detail;
    return failure;
}
function caughtArcResponse(error) {
    return {
        status: 'error',
        code: '500',
        description: error != null &&
            typeof error === 'object' &&
            'message' in error &&
            typeof error.message === 'string'
            ? error.message
            : 'Internal Server Error'
    };
}
/**
 * Represents an ARC transaction broadcaster.
 */
export default class ARC {
    URL;
    apiKey;
    deploymentId;
    callbackUrl;
    callbackToken;
    headers;
    httpClient;
    constructor(URL, config) {
        this.URL = URL;
        if (typeof config === 'string') {
            this.apiKey = config;
            this.httpClient = defaultHttpClient();
            this.deploymentId = defaultDeploymentId();
            this.callbackToken = undefined;
            this.callbackUrl = undefined;
        }
        else {
            const configObj = config ?? {};
            const { apiKey, deploymentId, httpClient, callbackToken, callbackUrl, headers } = configObj;
            this.apiKey = apiKey;
            this.httpClient = httpClient ?? defaultHttpClient();
            this.deploymentId = deploymentId ?? defaultDeploymentId();
            this.callbackToken = callbackToken;
            this.callbackUrl = callbackUrl;
            this.headers = headers;
        }
    }
    /**
     * Constructs a dictionary of the default & supplied request headers.
     */
    requestHeaders() {
        const headers = {
            'Content-Type': 'application/json',
            'XDeployment-ID': this.deploymentId
        };
        if (this.apiKey != null && this.apiKey !== '') {
            headers.Authorization = `Bearer ${this.apiKey}`;
        }
        if (this.callbackUrl != null && this.callbackUrl !== '') {
            headers['X-CallbackUrl'] = this.callbackUrl;
        }
        if (this.callbackToken != null && this.callbackToken !== '') {
            headers['X-CallbackToken'] = this.callbackToken;
        }
        if (this.headers != null) {
            for (const key in this.headers) {
                headers[key] = this.headers[key];
            }
        }
        return headers;
    }
    /**
     * Broadcasts a transaction via ARC.
     *
     * @param {Transaction} tx - The transaction to be broadcasted.
     * @returns {Promise<BroadcastResponse | BroadcastFailure>} A promise that resolves to either a success or failure response.
     */
    async broadcast(tx) {
        const requestOptions = {
            method: 'POST',
            headers: this.requestHeaders(),
            data: { rawTx: transactionHex(tx) }
        };
        try {
            const response = await this.httpClient.request(`${this.URL}/v1/tx`, requestOptions);
            return response.ok
                ? successfulArcResponse(response.data)
                : failedArcResponse(response.status, response.data);
        }
        catch (error) {
            return caughtArcResponse(error);
        }
    }
    /**
     * Broadcasts multiple transactions via ARC.
     * Handles mixed responses where some transactions succeed and others fail.
     *
     * @param {Transaction[]} txs - Array of transactions to be broadcasted.
     * @returns {Promise<Array<object>>} A promise that resolves to an array of objects.
     */
    async broadcastMany(txs) {
        const rawTxs = txs.map(tx => ({ rawTx: transactionHex(tx) }));
        const requestOptions = {
            method: 'POST',
            headers: this.requestHeaders(),
            data: rawTxs
        };
        try {
            const response = await this.httpClient.request(`${this.URL}/v1/txs`, requestOptions);
            return response.data;
        }
        catch (error) {
            const errorResponse = caughtArcResponse(error);
            return txs.map(() => errorResponse);
        }
    }
}
//# sourceMappingURL=ARC.js.map